import "./styles.scss";

import classnames from "classnames";
import { Link } from "react-router-dom";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Button from "components/Button";
import Loader from "components/Loader";
import PriorityFlag from "components/PriorityFlag";
import TrackerButton from "components/TrackerButton";
import SelectablePill from "components/SelectablePill";
import StatusPill from "components/StatusPill";
import Calendar from "components/Calendar";

import website_status_data from "libs/website_status_data.json";

import { ReactComponent as NoDataIcon } from "assets/icons/no-data.svg";
import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as SortIcon } from "assets/icons/new/arrow-sort.svg";
import { ReactComponent as ViewIcon } from "assets/icons/new/view.svg";

const mainClass = "table";

const Table = ({
  data,
  header,
  header_aside,
  task_onboarding,
  filters,
  filters_new,
  filter_default_open = false,
  loading,
  sort,
  setSort,
  filters_always_show,
}) => {
  const { t } = useTranslation();

  const [filters_show, setFiltersShow] = useState(filter_default_open);

  const current_sort = sort?.split(":");

  const table_value_type = (type, value, is_task_onboarding) => {
    switch (type) {
      case "button":
        return (
          <Button
            external_href={!!value?.external}
            href={value?.href}
            onClick={value?.onclick}
            loading={value?.loading}
            size="small"
          >
            {value?.label}
          </Button>
        );
      case "status":
        return <StatusPill active={!!value?.status} onClick={value?.onclick} />;
      case "pill":
        return <SelectablePill {...value} />;
      case "priority":
        return <PriorityFlag priority={value} />;
      case "link":
        return (
          <Link
            className={`${mainClass}__link`}
            to={
              value?.href
                ? `${value.href}${is_task_onboarding ? "?onboarding=task" : ""}`
                : "#"
            }
          >
            {value?.label}
          </Link>
        );
      case "clickable_text":
        const icon = value?.icon;

        return (
          <span
            onClick={
              typeof value?.onclick === "function"
                ? () => value?.onclick(undefined, is_task_onboarding)
                : undefined
            }
            className={`${mainClass}__clickable_text`}
          >
            {value?.label}
            {typeof icon !== "boolean" && icon !== false && (
              <span
                className={classnames({
                  [icon]: !!icon && typeof icon === "string",
                })}
              >
                {icon === "view" ? <ViewIcon /> : <ArrowIcon />}
              </span>
            )}
          </span>
        );
      case "website_status":
        const website_status = website_status_data?.find(
          (item) => item?.value === value,
        );
        return (
          <span
            className={classnames(`${mainClass}__website_status`, {
              [`${mainClass}__website_status--${website_status?.key}`]: !!value,
            })}
          >
            {t(website_status?.label)}
          </span>
        );
      case "website_items":
        return value?.length > 0 ? (
          <div className={`${mainClass}__website_items`}>
            {value?.map((item, index) => (
              <div key={index} className={`${mainClass}__website_items__item`}>
                <span />
                <Link to={item?.href}>{item?.label}</Link>
              </div>
            ))}
          </div>
        ) : (
          "-"
        );
      case "time_tracker":
        return (
          <div className={`${mainClass}__time-tracker`}>
            <TrackerButton
              active={value?.active}
              time={value?.label}
              onClick={value?.onclick}
              disable_tracking={value?.disable_tracking}
              start_time_tracked={value?.start_time_tracked}
            />
          </div>
        );
      case "hidden_id":
        return null;
      case "due_date":
        return (
          <div className={`${mainClass}__due-date`}>
            <p>
              {value?.due_date
                ? moment(value.due_date).format("LL")
                : !value?.isCompletelyDisabled
                  ? t("Select date")
                  : "-"}
            </p>
            {!value?.isCompletelyDisabled && (
              <Calendar
                only_icon
                autoPlacement
                default_value={value?.due_date}
                onChange={value?.onChange}
              />
            )}
          </div>
        );
      case "html":
        return <span dangerouslySetInnerHTML={{ __html: value }} />;
      default:
        return <span>{value}</span>;
    }
  };

  const sortHandler = (key) =>
    setSort(
      `${key}:${
        current_sort?.[0] === key && current_sort?.[1] === "asc"
          ? "desc"
          : "asc"
      }`,
    );

  return (
    <div className={mainClass}>
      {(!!header || !!header_aside || !!filters) && (
        <>
          <div className={`${mainClass}__header`}>
            <div className={`${mainClass}__header__content`}>
              {header}
              {(!!filters_show || filters_always_show) && (
                <div className={`${mainClass}__header__content__filters`}>
                  {filters}
                </div>
              )}
            </div>
            {!!header_aside && (
              <div className={`${mainClass}__header__aside`}>
                {header_aside}
              </div>
            )}
            {!!filters && !filters_always_show && (
              <div className={`${mainClass}__header__aside`}>
                <Button
                  size="small"
                  variant="transparent"
                  onClick={() => setFiltersShow((prev) => !prev)}
                >
                  {!!filters_show ? t("Less filters") : t("More filters")}
                </Button>
              </div>
            )}
          </div>
        </>
      )}
      {filters_new && (
        <div className={`${mainClass}__header`}>{filters_new}</div>
      )}
      <div className={`${mainClass}__content`}>
        <div className={`${mainClass}__content__wrapper`}>
          <div
            className={classnames(
              `${mainClass}__row`,
              `${mainClass}__row--header`,
            )}
          >
            {data?.keys?.map(
              ({ key, sort_key: table_sort_key, value }, index) => {
                const sort_key = table_sort_key || key;
                const isSortDisabled =
                  typeof table_sort_key === "boolean" &&
                  table_sort_key === false;

                return (
                  <div
                    key={index}
                    onClick={
                      value && !!setSort && !isSortDisabled
                        ? () => sortHandler(sort_key)
                        : null
                    }
                    className={classnames(
                      `${mainClass}__col`,
                      `${mainClass}__col--${key}`,
                      {
                        [`${mainClass}__col--${current_sort?.[1]}`]:
                          sort_key === current_sort?.[0],
                        [`${mainClass}__col--disabled`]: isSortDisabled,
                      },
                    )}
                  >
                    <span>
                      {value}
                      {!!value && !!setSort && !isSortDisabled && (
                        <SortIcon className={`${mainClass}__col__arrow`} />
                      )}
                    </span>
                  </div>
                );
              },
            )}
          </div>
          {!!loading ? (
            <div className="table-loader">
              <Loader color="blue" center size="large" />
            </div>
          ) : data?.items?.length ? (
            data?.items?.map((elements, index) => {
              const id = elements.find(({ key }) => key === "id")?.value;
              const is_task_onboarding = id ? id === task_onboarding : false;

              return (
                <div
                  key={index}
                  className={classnames(`${mainClass}__row`, {
                    website_created: is_task_onboarding,
                  })}
                >
                  {elements?.map(({ key, value, type }, index) =>
                    type !== "hidden_id" ? (
                      <Fragment key={index}>
                        <div
                          className={classnames(
                            `${mainClass}__col`,
                            `${mainClass}__col--${key}`,
                          )}
                        >
                          {table_value_type(type, value, is_task_onboarding)}
                        </div>
                      </Fragment>
                    ) : null,
                  )}
                </div>
              );
            })
          ) : (
            <div className="table-empty">
              <NoDataIcon />
              <span>{t("No data")}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Table;
